import React, { Fragment, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Splitter } from "@progress/kendo-react-layout";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  ToolbarSpacer,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { DropDownListById } from "../../forms/form-components";
import { Form } from "../../components/form";
import { List } from "../../components/list";
import { Input } from "@progress/kendo-react-inputs";
import { ExpressionsPageForm } from "./expressionspageform";
import { getDataTypeIcon, getExpressionIcon } from "../../lib/utils";
import { IntroPage } from "../intropage";
import { HelpPage } from "../helppage";
import {
  canCreateResolvedExpressions,
  canDeleteResolvedExpressions,
  canEditExpressions,
  canEditResolvedExpressions,
  canViewResolvedExpressions,
  expressionMaxReached,
  reportsMaxReached,
} from "../../routing/authorizationhandlers";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const datasetItemRender = (li, itemProps) => {
  let itemIcon = "";

  if (itemProps.dataItem.CategoryId == 1)
    //table
    itemIcon = "grid";
  else if (itemProps.dataItem.CategoryId == 2)
    //view
    itemIcon = "group";
  else if (itemProps.dataItem.CategoryId == 3)
    //SP
    itemIcon = "html";
  //"none"
  else itemIcon = "minus";

  const itemChildren = (
    <div
      className="datasets-list-item"
      style={{ display: "flex", alignItems: "center", padding: "0px 6px" }}
    >
      <div
        style={{
          marginRight: 12,
        }}
        className={`k-icon k-i-${itemIcon}`}
      ></div>
      <div>{itemProps.dataItem.Title}</div>
    </div>
  );

  return React.cloneElement(li, li.props, itemChildren);
};

const toolbarRenderer = (props) => {
  const { currentTheme, currentUser, pageState, t } = props;

  const saveTitle =
    pageState.mode == 1 ? t("expressions.save") : t("expressions.update");

  const toolsVisibleIcon = pageState.toolsVisible ? "unpin" : "pin";
  const toolsVisibleTitle = pageState.toolsVisible
    ? t("expressions.hide")
    : t("expressions.show");

  return (
    <Toolbar>
      <ToolbarItem>
        <Button
          disabled={pageState.processing}
          style={{ color: currentTheme.primary }}
          icon={toolsVisibleIcon}
          onClick={props.onToolsToggle}
        >
          {toolsVisibleTitle}
        </Button>
      </ToolbarItem>
      <ToolbarSeparator />
      {currentUser.IsAuthenticated &&
        canCreateResolvedExpressions(currentUser) && (
          <ToolbarItem>
            <Button
              disabled={
                pageState.processing || expressionMaxReached(currentUser)
              }
              style={{ color: currentTheme.primary }}
              icon="plus"
              onClick={props.onNew}
            >
              {t("expressions.create")}
            </Button>
          </ToolbarItem>
        )}
      {!pageState.formValues.IsSystem &&
        currentUser.IsAuthenticated &&
        ((pageState.mode == 1 && canCreateResolvedExpressions(currentUser)) ||
          (pageState.mode == 2 && canEditResolvedExpressions(currentUser))) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing}
                style={{ color: currentTheme.primary }}
                icon="spell-checker"
                onClick={props.onTestExpression}
              >
                {t("expressions.test")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {!pageState.formValues.IsSystem &&
        currentUser.IsAuthenticated &&
        ((pageState.mode == 1 && canCreateResolvedExpressions(currentUser)) ||
          (pageState.mode == 2 && canEditResolvedExpressions(currentUser))) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={
                  pageState.processing ||
                  (pageState.mode == 1 && expressionMaxReached(currentUser))
                }
                style={{ color: currentTheme.primary }}
                icon="save"
                onClick={props.onSave}
              >
                {saveTitle}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {pageState.mode == 2 &&
        currentUser.IsAuthenticated &&
        canCreateResolvedExpressions(currentUser) &&
        canViewResolvedExpressions(currentUser) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={
                  pageState.processing || reportsMaxReached(currentUser)
                }
                style={{ color: currentTheme.primary }}
                icon="backward-element"
                onClick={props.onClone}
              >
                {t("expressions.clone_exp")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {!pageState.formValues.IsSystem &&
        pageState.mode == 2 &&
        currentUser.IsAuthenticated &&
        canDeleteResolvedExpressions(currentUser) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing}
                style={{ color: currentTheme.primary }}
                icon="delete"
                onClick={(e) => props.onDelete(null)}
              >
                {t("expressions.delete2")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
    </Toolbar>
  );
};

const getEntityOperations = (expression, currentUser, t) => {
  let result = [];

  if (expression.IsSystem) {
    if (
      currentUser.IsAuthenticated &&
      (canViewResolvedExpressions(currentUser) ||
        canEditResolvedExpressions(currentUser))
    )
      result.push({ id: 1, text: t("expressions.view"), icon: "eye" });
  } else {
    if (
      currentUser.IsAuthenticated &&
      (canViewResolvedExpressions(currentUser) ||
        canEditResolvedExpressions(currentUser))
    )
      result.push({ id: 2, text: t("expressions.view"), icon: "eye" });

    if (
      currentUser.IsAuthenticated &&
      canDeleteResolvedExpressions(currentUser)
    )
      result.push({ id: 3, text: t("expressions.delete3"), icon: "delete" });
  }

  return result;
};

const entityMenuItemRenderer =
  (currentTheme, expressionId, onItemClicked) => (props) => {
    if (props.item.text)
      return (
        <div
          style={{ display: "flex", width: "100%", padding: "0px 6px" }}
          onClick={(e) => onItemClicked(expressionId, props.item)}
        >
          <span
            className={`k-icon k-i-${props.item.icon}`}
            style={{ display: "inline-block" }}
          ></span>
          <div style={{ marginLeft: 12 }}>{props.item.text}</div>
        </div>
      );
    else return <div className="menu-separator" style={{}} />;
  };

const entitiesItemRenderer =
  (currentTheme, currentUser, t) => (props, item, index) => {
    const dataTypeIcon = getDataTypeIcon(item.DataTypeId);
    const expressionIcon = getExpressionIcon(item.IsAggregate);

    //items are always visible in the left-hand list;  the Edit/Delete item menu appear based on the permission and so for double click
    return (
      <div
        key={item.Id}
        className={`list-item`}
        onDoubleClick={(e) => {
          if (
            currentUser.IsAuthenticated &&
            (canViewResolvedExpressions(currentUser) ||
              canEditResolvedExpressions(currentUser))
          )
            props.onExpressionDoubleClick(item.Id);
        }}
      >
        <div
          className={`list-item-data-type-icon k-icon ${dataTypeIcon} `}
        ></div>
        <div
          className={`list-item-expression-icon k-icon ${expressionIcon}`}
        ></div>
        <div className="list-item-title">{item.Title}</div>
        {currentUser.IsAuthenticated &&
          (canViewResolvedExpressions(currentUser) ||
            canEditExpressions(currentUser) ||
            canDeleteResolvedExpressions(currentUser)) && (
            <div className="list-item-operations">
              <DropDownButton
                className="buttons-container-button"
                items={getEntityOperations(item, currentUser, t)}
                itemRender={entityMenuItemRenderer(
                  currentTheme,
                  item.Id,
                  (expressionId, _item) => {
                    if (_item.id == 1 || _item.id == 2)
                      //view
                      props.onEntitySelected(expressionId, _item);
                    else if (_item.id == 3)
                      // delete
                      props.onDelete(expressionId, _item);
                  }
                )}
                icon="more-horizontal"
                fillMode={"flat"}
              />
            </div>
          )}
      </div>
    );
  };

const columnsItemRenderer = (currentTheme) => (props, item, index) => {
  const dataTypeIcon = getDataTypeIcon(item.DataTypeId);

  return (
    <div key={item.Id} className={`list-item`}>
      <div className={`list-item-data-type-icon k-icon ${dataTypeIcon} `}></div>
      <div className="list-item-title">{item.Name}</div>
    </div>
  );
};

export const ExpressionsPageBody = (props) => {
  const {
    onSplitterChange,
    dataSetsPanelRef,
    onDatasetChange,
    onCurrentFieldGroupChange,
    onNew,
    onTestExpression,
    onSave,
    onClone,
    onDelete,
    onErrorbarClose,
    onToolsToggle,
    onInsertCodeSnippet,
    onInsertFunction,
    onEntitySelected,
    onExpressionDoubleClick,
    onDataTypeChange,
    onHelpersGroupToggle,
    introItems,
    helpItems,
    numericIsVisible,
    textIsVisible,
    dateIsVisible,
    dateTimeIsVisible,
    updateDirtyState,
    ...other
  } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.expressionsPage);
  const appearance = useSelector((state) => state.appearance);
  const datasets = pageState.lookups.datasets;
  const currentDataset = pageState.currentDataset;
  const currentDatasetId = currentDataset.Id;
  const [entityFilter, setEntityFilter] = useState("");
  const [showSystemEntities, setShowSystemEntities] = useState(true);
  const { t } = useTranslation();

  console.log("HelpItems", helpItems);

  // const introItems = useRef([
  //   {color: currentTheme.colorVariant1, icon: SqlIcon, width:64, height: 32, content: "An expression is any valid SQL statement that returns a boolean, numeric, text, date or date/time value."},
  //   {color: currentTheme.colorVariant2, icon: DatabaseIcon, width:90, height: 32, content: "Expressions are referenced by data sources. This is how the datasets are extended."},
  //   {color: currentTheme.colorVariant2, icon: AggregateIcon, width:100, height: 32, content: "An expression may or may not be an aggregate. An aggregate expression exists in a grouped data source where each field is either grouped by or is itself an aggregate."},
  //   {color: currentTheme.colorVariant3, icon: FunctionsIcon, width:80, height: 32, content: "Aggregate expressions include: Count, Count_Big, Min, Max, Avg, Sum, Approx_Count_Distinct, Grouping, Grouping_ID, StdDev, StdDevP."},
  //   {color: currentTheme.colorVariant3, icon: ConditionIcon, width:80, height: 32, content: "Other types of expressions include conditional statements (Case) as well as conversion, text and date functions."},
  //   {color: currentTheme.colorVariant3, icon: LockIcon, width:80, height: 32, content: "Once created, you cannot change the data type of an expression. If an alert/widget is attached, additional limitations are imposed when updating."},
  //   {color: currentTheme.colorVariant3, icon: GroupIcon, width:80, height: 32, content: "Similarly to any other field, an expression must be assigned to a field group; those include: basic, money, performance, conversion etc. depending on the dataset."},
  //   {color: currentTheme.colorVariant3, icon: HelpIcon, width:90, height: 32, content: "The following link https://learn.microsoft.com/en-us/sql/t-sql/functions/functions?view=sql-server-ver16 lists all T-SQL built-in functions."},
  // ]);

  // const helpItemsFixed = useRef([
  //     {color: currentTheme.colorVariant1, icon: SqlIcon, width:42, height: 42, content: "An expression is any valid SQL statement that returns a boolean, numeric, text, date or date/time value."},
  //     {color: currentTheme.colorVariant2, icon: DatabaseIcon, width:42, height: 42, content: "Expressions are referenced by data sources. This is how the datasets are extended."},
  //     {color: currentTheme.colorVariant2, icon: AggregateIcon, width:100, height: 64, content: "An expression may or may not be an aggregate. An aggregate expression exists in a grouped data source where each field is either grouped by or is itself an aggregate."},
  //     {color: currentTheme.colorVariant3, icon: FunctionsIcon, width:52, height: 52, content: "Aggregate expressions include: Count, Count_Big, Min, Max, Avg, Sum, Approx_Count_Distinct, Grouping, Grouping_ID, StdDev, StdDevP."},
  //     {color: currentTheme.colorVariant3, icon: HelpIcon, width:52, height: 52, content: "The following link https://learn.microsoft.com/en-us/sql/t-sql/functions/functions?view=sql-server-ver16 lists all T-SQL built-in functions."},
  // ]);

  // const helpItemsHelper = useRef(helpersMethods);

  // const [helpItems, setHelpItems] = useState(helpItemsFixed.current);

  // const [numericIsVisible, setNumericIsVisible] = useState(false);
  // const [textIsVisible, setTextIsVisible] = useState(false);
  // const [dateIsVisible, setDateIsVisible] = useState(false);
  // const [dateTimeIsVisible, setDateTimeIsVisible] = useState(false);

  const getPanelBarItems = () => {
    const panelBarItems = [];

    panelBarItems.push(
      <PanelBarItem
        className="panel-dataset"
        key="dataset"
        title={t("expressions.dataset")}
        expanded={true}
      >
        <div className="dataset">
          <DropDownListById
            iconClassName="k-icon k-i-arrow-s k-i-table"
            style={{ marginTop: 4 }}
            data={datasets}
            dataItemKey="Id"
            textField="Title"
            value={currentDatasetId}
            onChange={onDatasetChange}
            itemRender={datasetItemRender}
          />
          {currentDatasetId == 0 && (
            <div
              style={{ marginLeft: 3, color: `${currentTheme.touchColor}` }}
              className="k-form-hint k-text-center"
            >
              {t("expressions.select")}
            </div>
          )}
        </div>
      </PanelBarItem>
    );

    if (currentDataset.Id > 0) {
      panelBarItems.push(
        <PanelBarItem
          className="panel-expressions"
          key="expressions"
          title={t("expressions.title")}
          selected={false}
          expanded={false}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Input
              type="search"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.value)}
              style={{
                width: "calc(100% - 16px)",
                margin: "16px 8px 8px 8px",
                backgroundColor: "transparent",
              }}
              placeholder={t("expressions.search")}
            />
            <div style={{ marginTop: 2, marginBottom: 16, marginLeft: 10 }}>
              <Checkbox
                style={{ marginTop: -4, marginRight: 4 }}
                value={showSystemEntities}
                onChange={(e) => {
                  setShowSystemEntities(e.value);
                }}
              >
                {" "}
                {t("expressions.show_exp")}{" "}
              </Checkbox>
            </div>
            <List
              className="expressions"
              items={currentDataset.Expressions.filter((e) => {
                if (
                  (!entityFilter || entityFilter.trim() == "") &&
                  showSystemEntities
                )
                  return true;

                if (!entityFilter || entityFilter.trim() == "")
                  //do not show system entities
                  return e.IsSystem == false;

                //i am here => there is text
                const filter = entityFilter.toLowerCase();

                if (showSystemEntities)
                  return e.Title.toLowerCase().indexOf(filter) >= 0;
                else
                  return (
                    e.Title.toLowerCase().indexOf(filter) >= 0 &&
                    e.IsSystem == false
                  );
              })}
              itemRenderer={entitiesItemRenderer(currentTheme, currentUser, t)}
              onEntitySelected={onEntitySelected}
              onExpressionDoubleClick={onExpressionDoubleClick}
              onDelete={props.onDelete}
            />
          </div>
        </PanelBarItem>
      );

      panelBarItems.push(
        <PanelBarItem
          className="panel-columns"
          key="columns"
          title={t("expressions.fields")}
          selected={false}
          expanded={false}
        >
          <div
            style={{
              padding: "16px 10px 6px 10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ display: "flex", flex: 1 }}>
              <DropDownListById
                iconClassName="k-icon k-i-arrow-s k-i-filter-sm"
                style={{ marginTop: 0, color: `${currentTheme.componentText}` }}
                data={currentDataset.FieldGroups}
                dataItemKey="Id"
                textField="Title"
                value={pageState.currentFieldGroupId}
                onChange={onCurrentFieldGroupChange}
              />
            </div>
          </div>
          <List
            className="columns"
            items={
              pageState.currentFieldGroupId == 0
                ? currentDataset.Columns
                : currentDataset.Columns.filter(
                    (e) => e.FieldGroupId == pageState.currentFieldGroupId
                  )
            }
            itemRenderer={columnsItemRenderer(currentTheme)}
          />
        </PanelBarItem>
      );

      panelBarItems.push(
        <PanelBarItem
          className="panel-description"
          key="help"
          title={t("expressions.dataset_desc")}
          selected={false}
          expanded={false}
          style={{}}
        >
          <p className="description" style={{ padding: "6px 12px", margin: 0 }}>
            {currentDataset.Description}
          </p>
        </PanelBarItem>
      );
    }

    return panelBarItems;
  };

  return (
    <Fragment>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {pageState.pageLoaded && (
        <Splitter
          style={{ border: "none" }}
          panes={pageState.panes}
          onChange={onSplitterChange}
        >
          <div className="expressions-page-manager">
            <PanelBar
              ref={dataSetsPanelRef}
              style={{ border: "none" }}
              expanded={[".0", ".1", ".3"]}
            >
              {getPanelBarItems()}
            </PanelBar>
          </div>
          {currentDatasetId > 0 && (
            <Form
              className="expressions-page-form"
              currentTheme={currentTheme}
              currentUser={currentUser}
              toolbarRenderer={toolbarRenderer}
              t={t}
              onNew={onNew}
              onTestExpression={onTestExpression}
              onSave={onSave}
              onClone={onClone}
              onDelete={onDelete}
              errorMessages={pageState.formServerErrors}
              onErrorbarClose={onErrorbarClose}
              onToolsToggle={onToolsToggle}
              pageState={pageState}
            >
              {pageState.mode > 0 ? (
                <ExpressionsPageForm
                  onInsertCodeSnippet={onInsertCodeSnippet}
                  onDataTypeChange={onDataTypeChange}
                  updateDirtyState={updateDirtyState}
                />
              ) : (
                <Fragment></Fragment>
              )}
            </Form>
          )}
          {currentTheme.pageHelpVisible && currentDatasetId > 0 && (
            <HelpPage
              onInsertFunction={onInsertFunction}
              numericIsVisible={numericIsVisible}
              textIsVisible={textIsVisible}
              dateIsVisible={dateIsVisible}
              dateTimeIsVisible={dateTimeIsVisible}
              onGroupToggle={onHelpersGroupToggle}
              title=""
              items={helpItems}
            />
          )}
          {currentDatasetId == 0 && (
            <IntroPage title={t("expressions.title")} items={introItems} />
          )}
        </Splitter>
      )}
      <style>
        {`                  
                    .expressions-page > .content
                    {
                      
                    }

                     .expressions-page .k-splitter{
                      flex-direction:${
                        appearance.isRightToLeft ? "row-reverse" : "row"
                      } 
                    }
                      
                    .expressions-page .k-splitter .k-pane{
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                    }

                    .expressions-page .datasetview-page-wrapper{
                    border: ${currentTheme.pageFormBorder};   
                    height:100%; direction:${
                      appearance.isRightToLeft ? "rtl" : "ltr"
                    } 
                    
                    }  

                    .expressions-page > .content .expressions-page-manager
                    {
                      border: ${currentTheme.managerBorder};
                      border-radius: ${currentTheme.pageBorderRadius};
                      background-color: ${currentTheme.componentBgFaded};
                    }
                    
                    .expressions-page > .content .expressions-page-manager .k-dropdownlist
                    {
                      /*bg of datasets dropdown*/
                      background-color: transparent;
                      color: ${currentTheme.componentText};
                    }

                    /*.expressions-page .content .expressions-page-manager .k-dropdownlist:focus
                    {
                        box-shadow: none;   
                    }*/

                    .expressions-page > .content .expressions-page-manager .panel-expressions .k-input::placeholder
                    {
                        color: ${currentTheme.componentText};
                        /*font-style: italic;*/
                    }

                    .expressions-page > .content .expressions-page-manager .k-panelbar
                    {
                      /*required*/
                      background-color: transparent;
                    }

                    .expressions-page > .content .expressions-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link .k-icon
                    {
                      /*the arrow near every panel header*/
                      color: ${currentTheme.touchColor}!important;
                      margin-right: 6px;
                    }

                    .expressions-page > .content .expressions-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link
                    {
                      /*the span in the above <li> i.e. the title of every group in the manager: datasets, expressions, columns and description*/  
                      background-color: transparent;
                      color: ${currentTheme.touchColor};
                      border-bottom: 1px solid ${currentTheme.separatorColor}; 
                    }

                    .expressions-page > .content .expressions-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link:hover
                    {
                      /*the above hovered - do nothing*/
                    }

                    .expressions-page > .content .expressions-page-manager .expressions .list-item:hover
                    {
                      /*expression item hover */
                      background-color: ${currentTheme.componentBorderColor};
                      cursor: pointer;
                    }

                    .expressions-page > .content .expressions-page-manager .dataset,
                    .expressions-page > .content .expressions-page-manager .description,
                    .expressions-page > .content .expressions-page-manager .k-child-animation-container
                    {
                      /*bg color of exp. manager elements */
                    }

                    .expressions-page > .content .expressions-page-manager .list-container
                    {
                      /*bg of both the exp. and columns Lists*/
                      background-color: transparent;
                      padding : 0;/*can add padding to list items*/
                    }

                    .expressions-page > .content .expressions-page-manager .k-panelbar-header
                    {
                      background-color: transparent;
                    }

                    .expressions-page > .content .expressions-page-manager .k-panelbar-content
                    {
                      background-color: transparent;
                    }

                    .expressions-page > .content .expressions-page-manager .expressions .list-item-data-type-icon
                    {
                      /*first icon in expressions list*/
                      padding-left: 12px
                    }

                    .expressions-page > .content .expressions-page-manager .expressions .list-item-expression-icon
                    {
                      /*second icon in expressions list*/
                      padding: 5px 3px;                 
                    }

                    .expressions-page > .content .expressions-page-manager .expressions .list-item-title
                    {
                      vertical-align: middle;/*fine tuning*/
                    }

                    .expressions-page > .content .expressions-page-manager .columns
                    {
                      /*space above the first column in the list*/
                      padding-top: 6px;
                    }

                    .expressions-page > .content .expressions-page-manager .columns .list-item-data-type-icon
                    {
                      /*first icon in columns list*/
                      padding-left: 12px;
                    }

                    .expressions-page > .content .expressions-page-manager .columns .list-item-title
                    {
                    }                    

                    /*the form*/
                    .expressions-page-form   
                    {
                        background-color: transparent;
                        padding-left: 0px;
                        border: ${currentTheme.pageFormBorder};   
                        border-radius: ${currentTheme.pageBorderRadius};
                    }

                    /*form toolbar*/
                    .expressions-page-form > .toolbar .k-toolbar
                    {
                        width: 100%;
                        border-left: none !important;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        border-top: none !important;
                        border-right: none !important;
                        background-color: ${
                          currentTheme.toolbarBg
                        };/*transparent;*/
                    }

                    .expressions-page > .content .toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                    }

                    .expressions-page > .content .toolbar .k-button-text
                    {
                      /*color of the text in every toolbar button=black in azure theme*/
                      color: ${currentTheme.toolbarText};
                    }

                    .expressions-page > .content .toolbar .k-button-icon
                    {
                      font-weight: 500;
                      color: ${currentTheme.primary};
                    }

                    .expressions-page > .content .form-root .k-form .k-form-field:nth-child(1)
                    {
                        margin-top: 4px;
                    }

                    .expressions-page > .content .form-root .k-form .k-form-field
                    {
                        margin-top: 32px;
                    }

                    .expressions-page > .content .form-root .k-form .k-form-field:nth-child(6)                   
                    {
                        margin-top: -4px !important;
                        width: 100%;
                    }

                    .expressions-page > .content .form-root .content
                    {
                      background-color: ${currentTheme.pageContentBg};
                    }

                    /*styling the popup menu */
                    .k-popup ul{
                        padding: 0px;
                    }

                    .k-popup ul > li{
                        margin: 0;
                        padding: 0px;                                
                    }

                    .k-popup ul > li > span{
                        border: none !important;
                        box-shadow: none !important;
                    }

                    .k-menu-link{
                        padding: 0px 0px !important;
                    }

                    @media (max-width: 1024px) {
                      .expressions-page > .content {
                        overflow-y: auto;
                      }

                      .expressions-page > .content .k-splitter {
                        display: flex;
                        flex-direction: column !important;
                        height: auto !important;
                        margin-bottom: calc(64px + env(safe-area-inset-bottom));
                        overflow: visible;
                      }

                      .expressions-page > .content .k-splitbar {
                        display: none !important;
                      }

                      .expressions-page > .content .k-splitter > .k-pane {
                        flex: 0 0 auto !important;
                        width: 100% !important;
                        height: auto !important;
                        min-height: 0 !important;
                        min-width: 0 !important;
                        max-width: 100% !important;
                      }

                      .expressions-page > .content .k-pane:has(.help-page) {
                        display: none !important;
                      }

                      .expressions-page > .content .expressions-page-manager {
                        height: auto;
                        max-height: 45dvh;
                        margin-bottom: 12px;
                        overflow: auto;
                      }

                      .expressions-page > .content .expressions-page-manager .expressions .list-item-title,
                      .expressions-page > .content .expressions-page-manager .columns .list-item-title {
                        flex: 1;
                        min-width: 0;
                        max-width: none;
                      }

                      .expressions-page > .content .expressions-page-form {
                        height: auto;
                        min-height: 0;
                        overflow: visible;
                      }

                      .expressions-page > .content .expressions-page-form > .toolbar .k-toolbar {
                        flex-wrap: wrap;
                        gap: 4px;
                        padding: 6px;
                      }

                      .expressions-page > .content .expressions-page-form > .toolbar {
                        display: none;
                      }

                      .expressions-page > .content .expressions-page-form > .content {
                        overflow: auto;
                        pointer-events: auto;
                        padding-bottom: calc(80px + env(safe-area-inset-bottom));
                        box-sizing: border-box;
                      }

                      .expressions-page > .content .expressions-page-form > .content input,
                      .expressions-page > .content .expressions-page-form > .content textarea,
                      .expressions-page > .content .expressions-page-form > .content button,
                      .expressions-page > .content .expressions-page-form > .content .k-dropdownlist,
                      .expressions-page > .content .expressions-page-form > .content .toggle-container,
                      .expressions-page > .content .expressions-page-form > .content .list-item,
                      .expressions-page > .content .expressions-page-form > .content .list-item-delete {
                        pointer-events: none;
                      }

                      .expressions-page > .content .expressions-page-form > .toolbar .k-separator {
                        display: none;
                      }

                      .expressions-page > .content .intro-page-wrapper {
                        height: auto;
                        min-height: 0;
                      }

                      .expressions-page > .content .intro-page {
                        overflow: visible;
                        padding: 8px;
                      }

                      .expressions-page > .content .intro-page > .item {
                        width: 50%;
                        padding: 16px 10px;
                        margin-top: 0;
                      }
                    }
                  `}
      </style>
    </Fragment>
  );
};
